import { NbfcLink, linkifyNbfc } from "../NbfcLink";
import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";

const KYCAmlPolicy: React.FC = () => {
  const policySections = [
    {
      title: `KYC & AML Policy for ${import.meta.env.VITE_COMPANY_NAME}`,
      content: `${import.meta.env.VITE_COMPANY_NBFC} is an RBI-registered NBFC specializing in short-term personal loans.`,
    },
    {
      title: "1. Objective",
      content:
        "To prevent misuse of the NBFC’s services for money laundering, terrorist financing, and other unlawful activities by implementing robust Know Your Customer (KYC) and Anti-Money Laundering (AML) procedures.",
    },
    {
      title: "2. Regulatory Framework",
      list: [
        "RBI Master Direction – KYC, 2016 (updated as on August 14, 2025).",
        "Prevention of Money Laundering Act (PMLA), 2002.",
        "RBI’s AML/CFT guidelines and all subsequent circulars and amendments.",
      ],
    },
    {
      title: "3. Customer Acceptance Policy (CAP)",
      list: [
        "No account shall be opened anonymously or under fictitious/benami names.",
        "Mandatory collection of valid identity and address proof before loan disbursal.",
        "Enhanced due diligence for high-risk customers such as PEPs or NRIs.",
        "Accounts shall not be opened if the customer refuses to provide required documentation.",
      ],
    },
    {
      title: "4. Customer Identification Procedure (CIP)",
      list: [
        "Verification through valid KYC documents such as Aadhaar, PAN, Passport, or Voter ID.",
        "Biometric authentication via UIDAI where applicable.",
        "Video KYC permitted for onboarding, subject to regulatory compliance.",
        "Periodic re-KYC for existing customers based on their risk categorization.",
      ],
    },
    {
      title: "5. Risk Categorization",
      list: [
        "Low Risk: Salaried individuals and government employees.",
        "Medium Risk: Self-employed and small business owners.",
        "High Risk: Politically Exposed Persons (PEPs), cash-intensive businesses.",
        "Risk-based KYC updating frequency will apply accordingly.",
      ],
    },
    {
      title: "6. Monitoring of Transactions",
      list: [
        "Automated alert systems will identify suspicious activities such as frequent cash deposits or round-tripping.",
        "Threshold-based reports will be generated for FIU-IND.",
        "Real-time flagging of high-value or unusual transactions.",
      ],
    },
    {
      title: "7. Record Keeping",
      list: [
        "Maintain KYC records for at least 5 years post account closure.",
        "Transaction records retained for 5 years from the transaction date.",
        "All data will be securely stored and made available for regulatory inspection when required.",
      ],
    },
    {
      title: "8. Reporting to Authorities",
      list: [
        "Suspicious Transaction Reports (STRs) will be filed with FIU-IND within prescribed timelines.",
        "Cash Transaction Reports (CTRs) will be filed for cash transactions over ₹10 lakh.",
        "Compliance with RBI’s AML/CFT periodic reporting formats will be ensured.",
      ],
    },
    {
      title: "9. Employee Training & Accountability",
      list: [
        "Ongoing employee training on KYC/AML procedures, red flags, and escalation processes.",
        "Standard operating procedures for onboarding, documentation, and verification.",
        "Defined accountability and escalation matrix for credit and operations teams.",
      ],
    },
    {
      title: "10. Internal Audit & Review",
      list: [
        "Quarterly internal audits focused on KYC/AML compliance effectiveness.",
        "Annual policy review by senior management and the Board.",
        "Timely corrective measures for any lapses or identified deficiencies.",
      ],
    },
  ];

  return (
    <div className="min-w-screen bg-[var(--background-gradient-middle)]">
      <div className="max-w-[1440px] mx-auto flex flex-col gap-12 px-4 sm:px-6 lg:px-12 py-16">
        <Navbar />

        {/* Hero Section */}
        <div className="text-center relative z-10 w-full mb-2 mt-12">
          <h1 className="text-4xl md:text-7xl font-[900] leading-none tracking-tighter mb-4 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary-accent)] text-transparent bg-clip-text">
            KYC & AML Policy
          </h1>

          <p className="text-[var(--text-gray)] font-bold text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            At{" "}
            <span className="font-semibold text-[var(--primary)] text-xl">
              {import.meta.env.VITE_COMPANY_NAME}
            </span>
            , operated by{" "}
            <span className="text-[var(--primary)] text-xl">
              <NbfcLink />
            </span>
            , we adhere to RBI’s Know Your Customer (KYC) and Anti-Money Laundering
            (AML) guidelines to maintain transparency, integrity, and trust in all
            financial dealings.
          </p>
        </div>

        {/* Policy Content */}
        <div className="max-w-4xl w-full mx-auto">
          <div className="bg-white/40 backdrop-blur-3xl rounded-[32px] md:rounded-[40px] border-2 border-white/50 shadow-[20px_20px_40px_rgba(0,0,0,0.04)] p-8 md:p-12 space-y-10">
            {policySections.map((section, idx) => (
              <div key={idx} className="space-y-4">
                <h2 className="text-2xl font-[900] text-[var(--text-primary)] tracking-tight mb-2 border-b-2 border-[var(--primary)]/20 pb-2">
                  {linkifyNbfc(section.title)}
                </h2>
                {section.content && (
                  <p className="text-[var(--text-gray)] text-base whitespace-pre-line leading-relaxed font-medium">
                    {linkifyNbfc(section.content)}
                  </p>
                )}
                {section.list && (
                  <ul className="list-disc list-outside ml-6 space-y-2 text-[var(--text-gray)] text-base font-medium">
                    {section.list.map((li, i) => (
                      <li key={i} className="pl-1">{linkifyNbfc(li)}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default KYCAmlPolicy;
